import { getCat, getCell } from '../session-storage';

export default class DisneyRewardsEventCatalog {
    eventCatalog = [];

    constructor() {
        /**
         * The `apply_now_click` event type captures metadata related to the guest's
         * site interactions leading up to their decision to apply for a credit
         * card using the regular apply method.
         *
         * Example:
         * `data-event_name="apply_now_click"`
         */
        this.eventCatalog.push({
            type: 'apply_now_click',
            getPayload: (eventEl) => this.getApplyNowData(eventEl)
        });

        /**
         * The `card_design` event captures a guest's currently-selected card type
         * when they click through to Chase's website from the `/card-designs/` page
         * to change their design.
         *
         * Note that this event type is currently applied directly to links by
         * editors in the CMS, meaning you may not find any other references to it
         * when searching the project.
         *
         * Example:
         * `data-event_name="card_design"`
         */
        this.eventCatalog.push({
            type: 'card_design',
            getPayload: () => ({
                card_art_selected: this.getCardArtData()
            })
        });

        /**
         * The `content_click` event type is the most generic, used to provide basic
         * identification of clicked elements or to occasionally assign arbitrary
         * data to specific elements of interest.
         *
         * Example:
         * `data-event_name="content_click"`
         * `data-content_link="learn_more_1"`
         */
        this.eventCatalog.push({
            type: 'content_click',
            getPayload: (eventEl) => ({
                content_link: eventEl.dataset.content_link
            })
        });

        /**
         * The `navigation_link` event type identifies interactions with navigation
         * elements that help guests get around within the site.
         *
         * Example:
         * `data-event_name="navigation_link_click"`
         * `data-navigation_link="home"`
         */
        this.eventCatalog.push({
            type: 'navigation_link_click',
            getPayload: (eventEl) => ({
                navigation_link: eventEl.dataset.navigation_link
            })
        });

        /**
         * The `footer_link` event type identifies interactions with elements in the
         * site's footer, such as links to the site's terms & conditions,
         * corporate information pages, or required legal disclosures.
         *
         * Example:
         * `data-event_name="footer_link_click"`
         * `data-footer_link="site_map"`
         */
        this.eventCatalog.push({
            type: 'footer_link',
            getPayload: (eventEl) => ({
                footer_link: eventEl.dataset.footer_link
            })
        });

        /**
         * The `change_card_design` event captures when a guest clicks
         * a button to change their card design, including which card art is
         * currently selected.
         *
         * This event is automatically applied to buttons with
         * `data-content_link` matching the pattern "change_card_design_*"
         *
         * Example:
         * `data-content_link="change_card_design_1"`
         * `data-content_link="change_card_design_2"`
         */
        this.eventCatalog.push({
            type: 'change_card_design',
            getPayload: (eventEl) => ({
                content_link: eventEl.dataset.content_link,
                card_art_selected: this.getCardArtData(),
                card_type: this.getCardTypeData()
            })
        });

        /**
         * The `new_card_order` event captures when a guest clicks
         * the button to order a new card (cardmember transfer DRD).
         *
         * This event is automatically applied to buttons with class
         * `wp-block-disney-rewards-cardmember-transfer-drd__button`
         */
        this.eventCatalog.push({
            type: 'new_card_order',
            getPayload: (eventEl) => ({
                button_text: eventEl.textContent?.trim() || 'Order New Card',
                button_url: eventEl.href || '',
                card_type: this.getCardTypeData()
            })
        });
    }

    getApplyNowData(eventEl) {
        return {
            card_art_selected: this.getCardArtData(),
            card_type: this.getCardTypeData(),
            cell: this.getCellCodeData(eventEl)
        };
    }

    getCardArtData() {
        let activeItem = [];

        const carousels = [
            document.querySelector('[data-block-type="coverflow-carousel"]'),
            document.querySelector('[data-block-type="filmstrip-carousel"]'),
            document.querySelector('[data-block-type="filmstrip-carousel-cm"]')
        ].filter((element) => element !== null);
        if (carousels.length) {
            activeItem = Array.from(carousels[0].querySelectorAll('.carousel-item')).filter((item) =>
                item.className.includes('active')
            );
        }

        const grid = document.querySelector('[data-block-type="card-design-grid"]');
        if (grid) {
            activeItem = Array.from(grid.querySelectorAll('.grid-item')).filter((item) =>
                item.className.includes('active')
            );
        }

        return activeItem.length ? activeItem[0].dataset.cardDesignTitle : 'unknown';
    }

    getCardDesignData(dataset) {
        const { cardDesignId, cardDesignTitle, cardDesignAcf } = dataset;

        return {
            id: cardDesignId,
            title: cardDesignTitle,
            ...JSON.parse(cardDesignAcf)
        };
    }

    getCardTypeData() {
        let cat = getCat().name;

        const applyNow = document.querySelector('[data-block-type="apply-now"]');
        if (applyNow) {
            cat = applyNow.querySelector('[data-card-type]').dataset.cardType;
        }

        return cat;
    }

    getCellCodeData(eventEl) {
        const cell = getCell().value;
        const href = eventEl.getAttribute('href');
        const cellMatches = href.match(/cellCode=([a-zA-Z0-9]+)/i);

        return cellMatches ? cellMatches[1].toLowerCase() : cell;
    }
}
